import { FC, useMemo, useState } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useFeeVersions, usePublicationSnapshot } from '../../hooks/useFees';
import { FeeConfigurationViewModel, FeeHudLineViewModel } from '../../models/fees';
import { comparePublications, PublicationSnapshot, toConfigurationExport, toHudLineExport } from '../../utils/feeComparisonDiff';
import { ComparisonTool, VersionOption } from './ComparisonTool';

const buildSnapshot = (
  hudLines: FeeHudLineViewModel[],
  configurations: FeeConfigurationViewModel[]
): PublicationSnapshot => {
  const hudLineExports = hudLines.map(toHudLineExport);
  const hudLineCodeById = new Map(hudLines.map((h) => [h.feeHudLineId, h.hudLine ?? '']));
  const configurationExports = configurations.map((config) =>
    toConfigurationExport(config, (config.feeHudLineId != null ? hudLineCodeById.get(config.feeHudLineId) : undefined) ?? '')
  );
  return { hudLines: hudLineExports, configurations: configurationExports };
};

export const ComparisonToolContainer: FC = () => {
  const { versionsQuery } = useFeeVersions();
  const versions = versionsQuery.data ?? [];

  const versionOptions = useMemo<VersionOption[]>(() => {
    const options = [...versions]
      .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))
      .map((v) => ({
        label: `${v.versionNumber}${v.description ? ` - ${v.description}` : ''}`,
        value: v.feeRulesVersionId,
      }));
    return [{ label: 'Draft', value: null }, ...options];
  }, [versions]);

  const mostRecentPublishedId = useMemo(() => {
    if (versions.length === 0) return null;
    return [...versions].sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))[0].feeRulesVersionId;
  }, [versions]);

  const [versionAId, setVersionAId] = useState<number | null>(null); // Draft, per ticket's "draft vs published" framing
  const [versionBId, setVersionBId] = useState<number | null>(null);
  const [showDiffsOnly, setShowDiffsOnly] = useState(true);
  const [jsonDiffsOnly, setJsonDiffsOnly] = useState(true);

  // Once versions load, default B to the most recent publication if the user hasn't touched it yet.
  // (React-sanctioned "adjust state during render" pattern: converges after one extra render and
  // stops re-firing once versionBId is no longer null.)
  const [hasUserSetB, setHasUserSetB] = useState(false);
  if (!hasUserSetB && versionBId === null && mostRecentPublishedId !== null) {
    setVersionBId(mostRecentPublishedId);
  }

  const { snapshotQuery: snapshotAQuery } = usePublicationSnapshot(versionAId);
  const { snapshotQuery: snapshotBQuery } = usePublicationSnapshot(versionBId);

  const snapshotA = useMemo<PublicationSnapshot | null>(() => {
    if (!snapshotAQuery.data) return null;
    return buildSnapshot(snapshotAQuery.data.hudLines, snapshotAQuery.data.configurations);
  }, [snapshotAQuery.data]);

  const snapshotB = useMemo<PublicationSnapshot | null>(() => {
    if (!snapshotBQuery.data) return null;
    return buildSnapshot(snapshotBQuery.data.hudLines, snapshotBQuery.data.configurations);
  }, [snapshotBQuery.data]);

  const comparison = useMemo(() => {
    if (!snapshotA || !snapshotB) return null;
    return comparePublications(snapshotA, snapshotB);
  }, [snapshotA, snapshotB]);

  const isLoading = versionsQuery.isLoading || snapshotAQuery.isLoading || snapshotBQuery.isLoading;

  if (isLoading && !comparison) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <ComparisonTool
      versionOptions={versionOptions}
      versionAId={versionAId}
      versionBId={versionBId}
      onVersionAChange={setVersionAId}
      onVersionBChange={(id) => {
        setVersionBId(id);
        setHasUserSetB(true);
      }}
      showDiffsOnly={showDiffsOnly}
      onShowDiffsOnlyChange={setShowDiffsOnly}
      jsonDiffsOnly={jsonDiffsOnly}
      onJsonDiffsOnlyChange={setJsonDiffsOnly}
      comparison={comparison}
      isRefreshing={isLoading}
    />
  );
};

export default ComparisonToolContainer;
